import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import type { SxProps } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import type { muiTheme } from "@polifonia/theme";
import { Navigate } from "@tanstack/react-router";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { ExternalLink } from "@/components/ExternalLink";
import { InfoBox } from "@/components/InfoBox";
import { Typography } from "@/components/Typography";
import { useEnableContractSigning } from "@/features/contract/useEnableContractSigning";
import { getLocaleFromLanguageCode } from "@/features/i18n/i18n";
import { interlude } from "@/interlude";
import { PageLayout } from "@/pages/components/PageLayout";
import { PageTitle } from "@/pages/components/PageTitle";

const styles = {
  centered: {
    display: "flex",
    justifyContent: "center",
    py: "48px",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

const SIGNED_AT_FORMAT: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  timeZoneName: "short",
};

const AgreementRecords: React.FC = function AgreementRecords() {
  const { t, i18n } = useTranslation();
  const agreementsQuery = interlude.contract.useGetAgreements();

  const locale = useMemo(
    () => getLocaleFromLanguageCode(i18n.language),
    [i18n.language],
  );

  // "Signed at" reads in the timezone the row was signed under, not the
  // viewer's and not the org's current one. Each row carries its own
  // orgTimezone precisely because an org's timezone can change, and this table
  // is the customer's record of when they signed — showing an older signature
  // in a newer timezone would misstate it. Formatters are cached per timezone
  // because rows usually share one.
  const formatSignedAt = useMemo(() => {
    const formatters = new Map<string, Intl.DateTimeFormat>();
    return (value: string, timeZone: string): string => {
      let formatter = formatters.get(timeZone);
      if (!formatter) {
        try {
          formatter = new Intl.DateTimeFormat(locale, {
            ...SIGNED_AT_FORMAT,
            timeZone,
          });
        } catch {
          // Unknown timezone name: fall back rather than blank the whole table.
          formatter = new Intl.DateTimeFormat(locale, SIGNED_AT_FORMAT);
        }
        formatters.set(timeZone, formatter);
      }
      return formatter.format(new Date(value));
    };
  }, [locale]);

  const contractTypeLabel = useMemo(
    () => ({
      msa: t("contract.type.msa", "Master Service Agreement"),
      addendum: t("contract.type.addendum", "Product Addendum"),
    }),
    [t],
  );

  if (agreementsQuery.isPending) {
    return (
      <Box sx={styles.centered}>
        <CircularProgress />
      </Box>
    );
  }

  if (agreementsQuery.isError) {
    return (
      <InfoBox
        variant="error"
        description={t(
          "contractCenter.error.load",
          "Signing records are not available right now. Please try again later.",
        )}
      />
    );
  }

  const agreements = agreementsQuery.data.agreements;

  if (agreements.length === 0) {
    return (
      <Typography variant="note">
        {t("contractCenter.empty", "No contracts have been signed yet.")}
      </Typography>
    );
  }

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>
              {t("contractCenter.table.contract", "Contract")}
            </TableCell>
            <TableCell>
              {t("contractCenter.table.product", "Product")}
            </TableCell>
            <TableCell>
              {t("contractCenter.table.version", "Version")}
            </TableCell>
            <TableCell>
              {t("contractCenter.table.signedBy", "Signed by")}
            </TableCell>
            <TableCell>
              {t("contractCenter.table.signedAt", "Signed at")}
            </TableCell>
            <TableCell>
              {t("contractCenter.table.contractFile", "Contract file")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {agreements.map((agreement) => (
            // One row per agreed contract: interlude enforces a unique
            // (organization, contract) constraint and this table shows a
            // single org, so contractId identifies the row on its own.
            <TableRow key={agreement.contractId} hover>
              <TableCell>{contractTypeLabel[agreement.type]}</TableCell>
              <TableCell>
                {agreement.product === "all"
                  ? t("contractCenter.allProducts", "All products")
                  : agreement.product.toUpperCase()}
              </TableCell>
              <TableCell>{agreement.agreedVersionKey}</TableCell>
              <TableCell>{agreement.userEmail}</TableCell>
              <TableCell>
                {formatSignedAt(agreement.createdAt, agreement.orgTimezone)}
              </TableCell>
              <TableCell>
                {agreement.pdfUrl ? (
                  <ExternalLink href={agreement.pdfUrl}>
                    {t("contractCenter.table.view", "View")}
                  </ExternalLink>
                ) : (
                  "—"
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const ContractCenterPage: React.FC = function ContractCenterPage() {
  const { t } = useTranslation();
  const isEnabled = useEnableContractSigning();

  // See the note in contractSigning/index.tsx: the route is gated here so it is
  // not reachable by URL for orgs without the feature.
  if (!isEnabled) {
    return <Navigate to="/applications" replace />;
  }

  // Signing always happens on the product redirect landing page (single
  // product); the Contract Center is the customer's record of what they
  // have already agreed to.
  return (
    <PageLayout>
      <PageTitle>{t("contractCenter.title", "Contract Center")}</PageTitle>
      <AgreementRecords />
    </PageLayout>
  );
};

export { ContractCenterPage };
